import { Color } from "src/games/shared/ColorValue";
import { GameState } from "../objects/GameState";
import bindAllOfThis from "src/utils/BindThisHelper";
import { TiledDropZone } from "src/games/shared/dropzone/DropZoneUtil";
import { TiledGameObject } from "src/games/shared/phaserHelpers/HandyTypes";
import TranslateApi from "src/utils/TranslateHelper";
import { PhaserModalConfig, PhaserModal } from "src/games/shared/Modal";
import { PhaserButtonConfig } from "src/games/shared/Button";
import { emit } from "cluster";
import { EndGameScene } from "src/games/DiseasedTreesGame/scenes/EndGameScene";

export class MainScene extends Phaser.Scene {
    gameWidth: number;
    gameHeight: number;
    background: Phaser.GameObjects.Image;
    map: Phaser.Tilemaps.Tilemap;
    bubble: Phaser.GameObjects.Text;
    bubbleBorder: Phaser.GameObjects.Image;
    markingTool: Phaser.GameObjects.Image;
    cleaningTool: Phaser.GameObjects.Image;
    treeGroup: Phaser.GameObjects.Group;
    gameOver: boolean = false;
    marking: boolean = false;
    cleaning: boolean = false;
    touchSupport: boolean = false;
    gameState: GameState;
    cursor: Phaser.GameObjects.Image;

    private translateString = "DiseasedTrees.Phaser.MainScene.";
    constructor() {
        super({
            key: "MainScene" + Math.random()
        });
    }

    preload(): void {
        this.load.image("pixel", require("/src/assets/qci/sprites/pixel.png"));
        this.load.atlas("pilots", require("/src/assets/qci/atlas/starPilotAll.png"), require("/src/assets/qci/atlas/starPilotAll.json"));
        this.load.atlas("assets", require("/src/assets/qci/atlas/gameDiseasedTrees.png"), require("/src/assets/qci/atlas/gameDiseasedTrees.json"));
        this.load.image("bubble", require('/src/assets/qci/sprites/phaser_bubble.png'));

        // Load maps
        this.load.tilemapTiledJSON('map1', require("/src/assets/qci/maps/diseaseTreesGame/diseased_trees_game_1.json"));
        this.load.tilemapTiledJSON('map2', require("/src/assets/qci/maps/diseaseTreesGame/diseased_trees_game_2.json"));
        this.load.tilemapTiledJSON('map3', require("/src/assets/qci/maps/diseaseTreesGame/diseased_trees_game_3.json"));

        // Load backgrounds
        this.load.image("background", require("/src/assets/qci/backgrounds/planet_r_top.jpg"));
    }

    create(): void {
        bindAllOfThis(this, MainScene.prototype);
        this.initialize();
        this.generateScene();
    }

    initialize() {
        this.gameWidth = this.textures.game.config.width as number;
        this.gameHeight = this.textures.game.config.height as number;
        // Check if we have touch support or not
        this.touchSupport = this.sys.game.device.input.touch;
        this.gameState = GameState.current;
        this.map = this.make.tilemap({ key: this.gameState.mapKey });
        this.treeGroup = null;
        this.gameOver = false;
    }

    generateScene(): void {
        this.background = this.add.image(0, 0, "background");
        this.background.setOrigin(0);
        this.background.setDisplaySize(this.gameWidth, this.gameHeight);
        this.addTools();
        this.addHealthyTrees();
        this.addDiseasedTrees();
        this.addObjects();
        // Create weather according to game state
        if (this.gameState.hasRain) {
            this.background.setAlpha(0.8);
            this.addRain();
        }
        if (this.gameState.hasSnow) {
            this.addSnow();
        }
        if (this.gameState.hasClouds) {
            this.addClouds();
        }
        if (this.gameState.hasFog) {
            this.addFog();
        }
        this.input.on('pointermove', this.pointerMove);
        this.useSprayer();
    }

    addTools(): void {
        var toolLayer = this.map.getObjectLayer('Tool Layer');
        toolLayer.objects.forEach((ob: TiledDropZone) => {
            if (ob.name === "toolBackground") {
                var background = this.add.image(ob.x, ob.y, "pixel");
                background.setTint(0x6eb8e5);
                background.setOrigin(0);
                background.setDisplaySize(ob.width, ob.height);

                var text = this.add.text(0, 0, TranslateApi.translate(this.translateString + "instructions"));
                text.setStyle({ fontSize: 20, fontFamily: "Russo One, Arial", align: "center" });
                text.setOrigin(0.5);
                text.setPosition(background.getCenter().x, background.getTopLeft().y - 2 * text.height);
                text.setWordWrapWidth(background.displayWidth, true);
            }
            else {
                var tool = this.add.image(ob.x, ob.y, "assets", ob.name);
                tool.setOrigin(0);
                tool.setDisplaySize(ob.width, ob.height);
                tool.setName(ob.name);
                tool.setInteractive();
                tool.setDepth(2);
                tool.setDataEnabled();
                tool.setFlipX(true);
                tool.setData('position', new Phaser.Geom.Point(tool.x, tool.y));

                if (tool.name === "sprayer") {
                    this.markingTool = tool;
                }
                else {
                    this.cleaningTool = tool;
                }

                // Make it easier to grab the tool by giving it a larger zone
                var zoneSize = 25;
                var toolZone = this.add.zone(tool.x - zoneSize / 2, tool.y - zoneSize / 2, tool.displayWidth + zoneSize, tool.displayHeight + zoneSize);
                toolZone.setOrigin(0);
                toolZone.setInteractive();
                toolZone.setDepth(10);
                toolZone.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
                    if (tool.name === "sprayer") {
                        this.useSprayer();
                    }
                    else {
                        this.useCleaningTool();
                    }
                });
            }
        });
    }

    useSprayer(): void {
        this.marking = true;
        this.cleaning = false;
        // Set this tool selected and the other is greyed out
        this.markingTool.clearTint();
        this.cleaningTool.setTint(Color.COLORS.get("light grey"));
        this.cursor = this.markingTool;
        var cleaningToolPosition = this.cleaningTool.getData('position');
        this.cleaningTool.setPosition(cleaningToolPosition.x, cleaningToolPosition.y);
        // Change cursor/mouse to the tool if we don't have touch support
        this.input.setDefaultCursor('none');
        this.cursor.setPosition(this.input.activePointer.position.x, this.input.activePointer.position.y);
        this.markingTool.setDepth(this.cleaningTool.depth + .01);
    }

    useCleaningTool(): void {
        this.marking = false;
        this.cleaning = true;
        // Set this tool selected and the other is greyed out
        this.markingTool.setTint(Color.COLORS.get("light grey"));
        this.cleaningTool.clearTint();
        this.cursor = this.cleaningTool;
        var markingToolPosition = this.markingTool.getData('position');
        this.markingTool.setPosition(markingToolPosition.x, markingToolPosition.y);
        // Change cursor/mouse to the tool if we don't have touch support
        this.input.setDefaultCursor('none');
        this.cursor.setPosition(this.input.activePointer.position.x, this.input.activePointer.position.y);
        this.cleaningTool.setDepth(this.markingTool.depth + .01);
    }


    addHealthyTrees(): void {
        var healthyTreeLayer = this.map.getObjectLayer("Healthy Trees");
        this.treeGroup = this.add.group();
        healthyTreeLayer.objects.forEach((ob: TiledGameObject) => {
            var healthyTree = this.add.image(ob.x, ob.y, "assets", "tree-" + Phaser.Math.Between(1, 5));
            healthyTree.setOrigin(0);
            healthyTree.setDisplaySize(ob.width, ob.height);
            healthyTree.setDataEnabled();
            healthyTree.setData('marked', false);
            healthyTree.setData('isDiseased', false);

            var zone = this.add.zone(healthyTree.x + healthyTree.displayWidth / 8, healthyTree.y, healthyTree.displayWidth * 0.75, healthyTree.displayHeight * 0.75);
            zone.setOrigin(0);
            zone.setInteractive();
            zone.setDepth(10);

            zone.on('pointerdown', () => {
                if (this.marking) {
                    this.markTree(healthyTree);
                }
                else if (this.cleaning) {
                    this.cleanTree(healthyTree);
                }
            });
            this.treeGroup.add(healthyTree);
        });
    }

    addDiseasedTrees(): void {
        var diseasedTreeLayer = this.map.getObjectLayer("Diseased Trees");
        diseasedTreeLayer.objects.forEach((ob: TiledGameObject) => {
            var diseasedTree = this.add.image(ob.x, ob.y, "assets", "tree-" + Phaser.Math.Between(1, 5));
            diseasedTree.setOrigin(0);
            diseasedTree.setDisplaySize(ob.width, ob.height);
            diseasedTree.setTint(0xff8e51);
            diseasedTree.setDataEnabled();
            diseasedTree.setData('marked', false);
            diseasedTree.setData('isDiseased', true);

            var zone = this.add.zone(diseasedTree.x + diseasedTree.displayWidth / 8, diseasedTree.y, diseasedTree.displayWidth * 0.75, diseasedTree.displayHeight * 0.75);
            zone.setOrigin(0);
            zone.setInteractive();
            zone.setDepth(10);

            zone.on('pointerdown', () => {
                if (this.marking) {
                    this.markTree(diseasedTree);
                }
                else if (this.cleaning) {
                    this.cleanTree(diseasedTree);
                }
            });
            this.treeGroup.add(diseasedTree);
        });
    }

    addObjects(): void {
        var layer = this.map.getObjectLayer("Astronaut Layer");
        layer.objects.forEach((ob: TiledGameObject) => {
            if (ob.name === "astronaut") {
                var astro = this.add.image(ob.x, ob.y, "pilots", "r");
                astro.setOrigin(0);
                astro.setDisplaySize(ob.width, ob.height);
            }
            else if (ob.name === "bubble") {
                this.bubble = this.add.text(ob.x, ob.y, "");
                this.bubble.setStyle({ color: "#ffffff", backgroundColor: "#000000", fontFamily: 'Russo One, Arial' });
                this.bubble.setPadding(20, 20, 20, 20);
                this.bubble.setWordWrapWidth(ob.width - 40, true);
                this.bubble.setDepth(20);

                this.bubbleBorder = this.add.image(this.bubble.x - 5, this.bubble.y - 5, "pixel");
                this.bubbleBorder.setOrigin(0);
                this.bubbleBorder.setTint(Color.PLANET_COLORS.get("R"));
                this.bubbleBorder.setDepth(20);

                this.bubble.setVisible(false);
                this.bubbleBorder.setVisible(false);
            }
        });
    }

    addSnow(): void {
        var particles, scale, gravity, y, lifespan;
        y = -10;
        particles = this.add.particles("assets", 'snowflake');
        scale = 0.2;
        gravity = 30;
        lifespan = 10000;

        particles.setDepth(10);

        var fall = {
            width: 100,
            height: 10,
            getPoints: (quantity) => {
                var cols = (new Array(fall.width)).fill(0);
                var lastCol = cols.length - 1;
                var Between = Phaser.Math.Between;
                var points = [];

                for (var i = 0; i < quantity; i++) {
                    var col = Between(0, lastCol);
                    points[i] = new Phaser.Math.Vector2(16 * col, y);
                }
                return points;
            }
        };

        particles.createEmitter({
            angle: 0,
            gravityY: gravity,
            lifespan: lifespan,
            scale: scale,
            blendMode: 'ADD',
            emitZone: { type: "edge", source: fall, quantity: 100, yoyo: true },
        });
    }

    addRain(): void {
        var rain, scale, gravity, y, lifespan;
        y = -10;
        rain = this.add.particles("assets", 'rain');
        scale = 0.6;
        gravity = 220;
        lifespan = 7000;

        rain.setDepth(10);

        var fall = {
            width: 100,
            height: 10,
            getPoints: (quantity) => {
                var cols = (new Array(fall.width)).fill(0);
                var lastCol = cols.length - 1;
                var Between = Phaser.Math.Between;
                var points = [];

                for (var i = 0; i < quantity; i++) {
                    var col = Between(0, lastCol);
                    points[i] = new Phaser.Math.Vector2(16 * col, y);
                }
                return points;
            }
        };

        rain.createEmitter({
            angle: 0,
            gravityY: gravity,
            lifespan: lifespan,
            scale: scale,
            blendMode: 'ADD',
            emitZone: { type: "edge", source: fall, quantity: 100, yoyo: true },
        });
    }



    addFog(): void {
        var fog = this.add.image(-400, 200, "assets", 'fog');
        fog.setOrigin(0);

        this.tweens.add({
            targets: fog,
            x: this.gameWidth,
            ease: 'Linear',
            duration: 300000,
            repeat: -1
        });
    }

    addClouds(): void {
        var clouds = this.add.image(-400, 10, "assets", 'clouds');
        clouds.setOrigin(0);
        this.tweens.add({
            targets: clouds,
            x: this.gameWidth,
            ease: 'Linear',
            duration: 200000,
            repeat: -1
        });
    }

    /**
     * Called when spray can overlaps with a tree
     * Adds the disease label
     * Shows feedback if not actually diseased
     */
    markTree(tree: Phaser.GameObjects.Image): void {
        if (!tree.getData("marked")) {
            var diseaseLabel = this.add.image(0, 0, "assets", "marker");
            diseaseLabel.setOrigin(0);
            diseaseLabel.setDisplaySize(30, 30);
            diseaseLabel.setTint(Color.COLORS.get("red"));
            if (tree.frame.name === "tree-1" || tree.frame.name === "tree-2" || tree.frame.name === "tree-4") {
                diseaseLabel.setPosition(tree.getCenter().x - diseaseLabel.displayWidth / 2, tree.getTopLeft().y + tree.displayHeight / 5);
            }
            else if (tree.frame.name === "tree-3") {
                diseaseLabel.setPosition(tree.getCenter().x - diseaseLabel.displayWidth * 0.75, tree.getTopLeft().y + tree.displayHeight / 8);
            }
            else if (tree.frame.name === "tree-5") {
                diseaseLabel.setPosition(tree.getCenter().x + diseaseLabel.displayWidth / 5, tree.getTopLeft().y + tree.displayHeight / 8);
            }
            tree.setData('marked', true);
            tree.setData('mark', diseaseLabel);

            if (!tree.getData('isDiseased')) {
                var bubbleText = TranslateApi.translate(this.translateString + "wrongMark");
                this.bubble.setText(bubbleText).setDepth(1);
                this.bubble.setVisible(true);
                this.bubbleBorder.setDisplaySize(this.bubble.displayWidth + 10, this.bubble.displayHeight + 10);
                this.bubbleBorder.setVisible(true).setDepth(.5);
            }
        }
    }

    /**
     * Called when spray can overlaps with a tree
     * Removes the disease label if there
     */
    cleanTree(tree: Phaser.GameObjects.Image): void {
        if (tree.getData('marked')) {
            this.animateCleaning(tree);
        }
    }

    animateCleaning(tree): void {
        var reaction = this.add.particles("bubble");
        var mark = tree.getData("mark");
        // Emit some bubbles
        var emitter: Phaser.GameObjects.Particles.ParticleEmitter = reaction.createEmitter({
            x: mark.getCenter().x,
            y: mark.getCenter().y,
            scale: 0.3,
            speed: 20,
            lifespan: 1200,
            blendMode: Phaser.BlendModes.ADD,
        });

        // Fade out the mark
        this.add.tween({
            targets: mark,
            duration: 1000,
            alpha: {
                getStart: () => 1,
                getEnd: () => 0
            },
            ease: "Linear",
            onComplete: () => {
                reaction.destroy();
                mark.destroy();
                tree.setData('marked', false);
            }
        });
    }

    isGameOver(): boolean {
        for (var tree of this.treeGroup.getChildren()) {
            if (tree.getData("isDiseased") && !tree.getData('marked')) {
                // Diseased tree isn't marked
                return false;
            }
            if (!tree.getData("isDiseased") && tree.getData('marked')) {
                // Healthy tree is incorrectly marked
                return false;
            }
        }
        return true;
    }

    pointerMove(): void {
        if (this.cursor != null) {
            if (this.marking) {
                this.cursor.setPosition(this.input.activePointer.position.x, this.input.activePointer.position.y);
            }
            else {
                this.cursor.setPosition(this.input.activePointer.position.x - this.cursor.displayWidth / 2, this.input.activePointer.position.y - this.cursor.displayHeight / 2);
            }
        }
    }


    update(): void {
        this.updateBubbleVisibility();
        this.checkIfGameIsOver();
    }

    checkIfGameIsOver(): void {
        var config = new PhaserModalConfig();
        config.buttonWidth = 100;
        var buttonConfig: PhaserButtonConfig = {
            backgroundColor: Color.KUDER_PRIMARY_BUTTON,
            smartSize: true
        };
        config.buttonConfig = buttonConfig;
        var options;

        // Check if the game is over
        if (this.isGameOver() && !this.gameOver) {
            this.cleaning = false;
            this.marking = false;
            this.cursor.setVisible(false);
            this.input.setDefaultCursor('default');
            this.removeAllInputListeners();

            config.title = TranslateApi.translate(this.translateString + "sceneDone");

            if (this.gameState.sceneIndex === 3) {
                options = [{ displayText: TranslateApi.translate("common.done"), id: 2 }];
            }
            else {
                options = [{ displayText: TranslateApi.translate("common.next"), id: 1 }];
            }

            var modal = new PhaserModal(this, 0, 0, options, config);
            modal.setPosition(this.gameWidth / 2 - modal.width / 2, this.gameHeight / 2 - modal.height / 2).setDepth(1);
            modal.setDepth(15);
            modal.on('optionClicked', (op) => {
                modal.destroy();
                if (op.id === 2) {
                    this.input.setDefaultCursor("default");
                    this.input.removeAllListeners();
                    this.scene.add("EndGameScene", EndGameScene, true);
                    this.scene.remove(this);
                }
                else {
                    // Next scene
                    this.scene.remove(this);
                    var nextSceneIndex = this.gameState.sceneIndex + 1;
                    this.gameState = GameState.GAME_STATES[nextSceneIndex - 1];
                    // Update global game state
                    GameState.current = this.gameState;
                    this.scene.add("MainScene", MainScene, true);
                }
            });
            this.gameOver = true;
            return;
        }
    }

    updateBubbleVisibility(): void {
        // If there are any healthy trees incorrectly marked, the bubble with feedback will show until they are all corrected
        var visible = false;
        for (var tree of this.treeGroup.getChildren()) {
            if (!tree.getData('isDiseased') && tree.getData('marked')) {
                visible = true;
            }
        }
        this.bubble.setVisible(visible);
        this.bubbleBorder.setVisible(visible);
    }

    removeAllInputListeners(): void {
        this.children.list.forEach((c) => {
            c.removeAllListeners();
        });
        this.input.removeAllListeners();
    }
}



// WEBPACK FOOTER //
// ./src/games/DiseasedTreesGame/scenes/MainScene.ts